import { CognitoUser } from 'amazon-cognito-identity-js'
import {
  axios,
  IDevice,
  IUserResponse,
  setCognitoUser,
  setCognitoUserSession,
} from '@getgreenline/homi-shared'
import {
  setCognitoUser as setSharedCognitoUser,
  setCognitoUserSession as setSharedCognitoUserSession,
} from '@getgreenline/shared'
import * as cognito from './cognito'

/** Inlined rather than taken from the deprecated Endpoints module, which has heavy load-time
 *  side effects and would make this file impossible to unit test in isolation. */
const GET_ME_URL = '/api/v1/user/me'
const LOGIN_DEVICE_SPOOF_URL = '/api/v1/login/device/spoof'

/**
 * "Spoof login" lets a Greenline admin log into a client's POS device by typing
 * `adminEmail///deviceName` into the device id field, with their own dashboard password.
 *
 * The admin is verified against Cognito here on the client - the API only ever sees the resulting
 * access token, never a password. See GR-13945: the API's legacy `///` branch bcrypt-checks
 * `users.password`, which the Cognito migration left null or stale for migrated admins.
 */
export const SPOOF_DELIMITER = '///'

export type SpoofErrorCode = 'FORMAT' | 'CREDENTIALS' | 'NOT_ADMIN' | 'NO_DEVICE' | 'UNAVAILABLE'

export class SpoofLoginError extends Error {
  constructor(public code: SpoofErrorCode, message: string, public deviceName?: string) {
    super(message)
    this.name = 'SpoofLoginError'
    // Required for `instanceof` to survive the TS -> ES5 downlevel of Error subclasses.
    Object.setPrototypeOf(this, SpoofLoginError.prototype)
  }
}

export interface ISpoofCredentials {
  adminEmail: string
  deviceName: string
}

/**
 * Returns undefined when the input is an ordinary device id, so the caller falls through to the
 * normal device login unchanged. Splits on the FIRST delimiter only, so a device legitimately
 * named `Front///Back` still resolves.
 */
export const parseSpoofDeviceId = (rawDeviceId: string): ISpoofCredentials | undefined => {
  const delimiterAt = rawDeviceId.indexOf(SPOOF_DELIMITER)
  if (delimiterAt === -1) return undefined

  const adminEmail = rawDeviceId.slice(0, delimiterAt).trim()
  const deviceName = rawDeviceId.slice(delimiterAt + SPOOF_DELIMITER.length).trim()

  if (!adminEmail || !deviceName) {
    throw new SpoofLoginError('FORMAT', `Expected <adminEmail>${SPOOF_DELIMITER}<deviceName>`)
  }

  return { adminEmail, deviceName }
}

const COGNITO_UNAVAILABLE_CODES = [
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'NotAuthorizedException_PasswordResetRequired',
]

const toSpoofError = (error: any, deviceName: string): SpoofLoginError => {
  if (error instanceof SpoofLoginError) return error

  const status = error?.response?.status
  if (status === 404) {
    return new SpoofLoginError('NO_DEVICE', 'Device not found', deviceName)
  }
  if (status === 401 || status === 403) {
    return new SpoofLoginError('NOT_ADMIN', 'Not a Greenline admin')
  }

  // `cognito.authenticateUser` rejects with this string when the account is mid new-password
  // challenge and we have no replacement password to supply.
  if (`${error}`.includes('New password is required')) {
    return new SpoofLoginError('UNAVAILABLE', 'New password required')
  }

  const code = error?.code || error?.name
  if (COGNITO_UNAVAILABLE_CODES.includes(code)) {
    return new SpoofLoginError('UNAVAILABLE', code)
  }
  if (code) {
    return new SpoofLoginError('CREDENTIALS', code)
  }

  return error
}

/**
 * Clears every trace of the admin's Cognito session.
 *
 * This is not optional hygiene. `setInterceptor` swaps a refreshed Cognito access token into the
 * shared axios `headers.common.Authorization` on every request whenever a cognitoUser is set, and
 * `getCurrentUser()` reads the session back out of localStorage. Left in place, the operator lands
 * in the POS, later navigates to a `/company/...` route, `CompanyRoot` calls `setupInterceptor()`
 * and the admin token silently replaces the device token - and that survives the page reload.
 */
const teardownSpoofSession = (cognitoUser: CognitoUser) => {
  setCognitoUser(null)
  setCognitoUserSession(null)
  setSharedCognitoUser(null)
  setSharedCognitoUserSession(null)

  try {
    cognitoUser.signOut()
  } catch {
    // signOut only clears local state; a failure here must not mask the login result.
  }

  window.localStorage.removeItem('authToken')
}

/**
 * Verifies the admin in Cognito, confirms they are a Greenline admin, then exchanges the access
 * token for a device session. The admin token is passed per-request only: axios merges
 * `config.headers` over `headers.common` AFTER request interceptors run, so this beats both the
 * global default and anything `setInterceptor` writes.
 */
export const spoofLoginDevice = async (
  adminEmail: string,
  deviceName: string,
  password: string,
): Promise<{ device: IDevice; authToken: string }> => {
  // `axios` is an `export let` reassigned by `setAPIUrl`, so this live binding always resolves to
  // the current instance - and therefore the current baseURL.
  const cognitoUser = cognito.createUser(adminEmail)

  try {
    const authenticationDetails = cognito.getAuthenticationDetails(adminEmail, password)
    const session = await cognito.authenticateUser(cognitoUser, authenticationDetails)
    const headers = {
      Authorization: `bearer ${session.getAccessToken().getJwtToken()}`,
    }

    const me = await axios.get<IUserResponse>(GET_ME_URL, { headers })
    if (!me.data.isAdmin) {
      throw new SpoofLoginError('NOT_ADMIN', `${adminEmail} is not a Greenline admin`)
    }

    const response = await axios.post(LOGIN_DEVICE_SPOOF_URL, { deviceId: deviceName }, { headers })

    return {
      device: response.data.device as IDevice,
      authToken: response.data.authToken as string,
    }
  } catch (error) {
    throw toSpoofError(error, deviceName)
  } finally {
    teardownSpoofSession(cognitoUser)
  }
}

/** Returns undefined for anything that is not a spoof failure, so callers can fall back. */
export const getSpoofErrorMessage = (error: unknown): string | undefined => {
  if (!(error instanceof SpoofLoginError)) return undefined

  switch (error.code) {
    case 'FORMAT':
      return `Spoof login must be in the format admin@email.com${SPOOF_DELIMITER}Device Name`
    case 'CREDENTIALS':
      return 'Incorrect admin email or password'
    case 'NOT_ADMIN':
      return 'This account is not a Greenline admin'
    case 'NO_DEVICE':
      return `No device found named "${error.deviceName}"`
    case 'UNAVAILABLE':
      return "This admin account can't be used for spoof login. Sign in to the dashboard first."
    default:
      return undefined
  }
}
